<script setup lang="ts">
import type { Product, ProductVariant } from '~/types'
import type { CustomFieldValueInput } from '~/utils/complexProductFields'

type GiftsterTip = {
  index: number
  name: string
  shortName: string
  price: number
  prefix: string
  allowsBatteries: boolean
  allowsAdaptor: boolean
  frameFile: string | null
}

type GiftsterPreview = {
  tipIndex: number
  tipName: string
  shortName: string
  basePrice: number
  previewDataUrl: string
}

type AccessoryOption = {
  key: string
  label: string
  batteries: 'NU' | 'DA'
  adaptor: 'NU' | 'DA'
  surcharge: number
}

type WizardStep = 'upload' | 'composing' | 'tip' | 'accessories' | 'preview'

type DesignPreset = 'auto' | 'photo' | 'logo'

const { get, post } = useApi()
const { formatPrice } = useSeo()
const { addToCart } = useCart()

const step = ref<WizardStep>('upload')
const loadingConfig = ref(true)
const composing = ref(false)
const composingPhase = ref('')
const addingToCart = ref(false)
const error = ref('')
const tips = ref<GiftsterTip[]>([])
const batterySurcharge = ref(0)
const adaptorSurcharge = ref(0)
const useEngravingSvgOnFrames = ref(false)
const previews = ref<GiftsterPreview[]>([])
const selectedTipIndex = ref<number | null>(null)
const selectedAccessoryKey = ref('none')
const designFile = ref<File | null>(null)
const processedDesignFile = ref<File | null>(null)
const designPreviewUrl = ref<string | null>(null)
const designPreset = ref<DesignPreset>('auto')
const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)

const presetOptions: Array<{ value: DesignPreset; label: string; hint: string }> = [
  { value: 'auto', label: 'Automat', hint: 'Detectează tipul imaginii' },
  { value: 'photo', label: 'Poza', hint: 'Portrete — dither Negative (natural pe LED)' },
  { value: 'logo', label: 'Logo / Siluetă', hint: 'Contururi / pattern regulat' },
]

const steps = [
  { id: 1, label: 'Încarcă' },
  { id: 2, label: 'Tip' },
  { id: 3, label: 'Accesorii' },
  { id: 4, label: 'Finalizează' },
]

const currentStep = computed(() => {
  if (step.value === 'upload' || step.value === 'composing') return 1
  if (step.value === 'tip') return 2
  if (step.value === 'accessories') return 3
  return 4
})

const selectedTip = computed(() =>
  tips.value.find((t) => t.index === selectedTipIndex.value) || null,
)

const selectedPreview = computed(() =>
  previews.value.find((p) => p.tipIndex === selectedTipIndex.value) || null,
)

const accessoryOptions = computed((): AccessoryOption[] => {
  const tip = selectedTip.value
  if (!tip) return []
  const options: AccessoryOption[] = [
    {
      key: 'none',
      label: 'Fără accesorii',
      batteries: 'NU',
      adaptor: 'NU',
      surcharge: 0,
    },
  ]
  if (tip.allowsBatteries) {
    options.push({
      key: 'batteries',
      label: 'Cu baterii',
      batteries: 'DA',
      adaptor: 'NU',
      surcharge: batterySurcharge.value,
    })
  }
  if (tip.allowsAdaptor) {
    options.push({
      key: 'adaptor',
      label: 'Cu adaptor',
      batteries: 'NU',
      adaptor: 'DA',
      surcharge: adaptorSurcharge.value,
    })
  }
  if (tip.allowsBatteries && tip.allowsAdaptor) {
    options.push({
      key: 'both',
      label: 'Baterii + adaptor',
      batteries: 'DA',
      adaptor: 'DA',
      surcharge: batterySurcharge.value + adaptorSurcharge.value,
    })
  }
  return options
})

const selectedAccessory = computed(() =>
  accessoryOptions.value.find((o) => o.key === selectedAccessoryKey.value)
  || accessoryOptions.value[0]
  || null,
)

const totalPrice = computed(() => {
  const base = selectedPreview.value?.basePrice ?? selectedTip.value?.price ?? 0
  return base + (selectedAccessory.value?.surcharge || 0)
})

async function loadConfig() {
  loadingConfig.value = true
  error.value = ''
  try {
    const response = await get<{
      tips: GiftsterTip[]
      batterySurcharge: number
      adaptorSurcharge: number
      useEngravingSvgOnFrames?: boolean
    }>('/giftster/config')
    tips.value = response.data?.tips || []
    batterySurcharge.value = Number(response.data?.batterySurcharge) || 0
    adaptorSurcharge.value = Number(response.data?.adaptorSurcharge) || 0
    useEngravingSvgOnFrames.value = response.data?.useEngravingSvgOnFrames === true
  } catch (err: unknown) {
    const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    error.value = message || 'Nu s-a putut încărca simulatorul'
  } finally {
    loadingConfig.value = false
  }
}

function setDesignFile(file: File | null) {
  if (!file) return
  if (!file.type.startsWith('image/') && !/\.svg$/i.test(file.name)) {
    error.value = 'Folosește o imagine PNG, JPG, WebP sau SVG'
    return
  }
  const maxBytes = 50 * 1024 * 1024
  if (file.size > maxBytes) {
    error.value = 'Imaginea este prea mare (maxim 50 MB)'
    return
  }
  designFile.value = file
  processedDesignFile.value = null
  if (designPreviewUrl.value) URL.revokeObjectURL(designPreviewUrl.value)
  designPreviewUrl.value = URL.createObjectURL(file)
  error.value = ''
  previews.value = []
  selectedTipIndex.value = null
}

function onPickFile(event: Event) {
  const input = event.target as HTMLInputElement
  setDesignFile(input.files?.[0] || null)
}

function onDrop(event: DragEvent) {
  isDragging.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file) setDesignFile(file)
}

async function prepareDesignFileForUpload(): Promise<File> {
  if (!designFile.value) throw new Error('Alege o imagine mai întâi')
  if (processedDesignFile.value) return processedDesignFile.value
  // Logo/siluetă: raw upload for clean potrace edges.
  if (designPreset.value === 'logo') {
    processedDesignFile.value = designFile.value
    return designFile.value
  }
  let file = designFile.value
  try {
    composingPhase.value = 'Ștergem fundalul în browser (AI)…'
    const { removeGiftsterBackground } = await import('~/utils/giftsterBgRemoval')
    file = await removeGiftsterBackground(file, (msg) => {
      composingPhase.value = msg
    })
  } catch {
    composingPhase.value = 'AI indisponibil — curățare automată pe server…'
  }
  processedDesignFile.value = file
  return file
}

async function startCompose() {
  if (!designFile.value) {
    error.value = 'Alege o imagine mai întâi'
    return
  }
  composing.value = true
  step.value = 'composing'
  composingPhase.value = 'Pregătim designul…'
  error.value = ''
  try {
    const file = await prepareDesignFileForUpload()
    composingPhase.value = designPreset.value === 'logo'
      ? 'Generăm silueta (potrace) pe rame…'
      : 'Aplicăm dither Negative tip gravură LED…'
    const body = new FormData()
    body.append('design', file)
    body.append('preset', designPreset.value)
    const response = await post<{ previews: GiftsterPreview[] }>(
      '/giftster/compose',
      body,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180_000 },
    )
    composingPhase.value = 'Compunem previzualizările pe tipuri…'
    previews.value = response.data?.previews || []
    if (!previews.value.length) {
      throw new Error('Nu s-au generat previzualizări')
    }
    selectedTipIndex.value = previews.value[0]!.tipIndex
    selectedAccessoryKey.value = 'none'
    step.value = 'tip'
  } catch (err: unknown) {
    const message = (err as { response?: { data?: { message?: string } }; message?: string })
      ?.response?.data?.message
      || (err as { message?: string })?.message
    error.value = message || 'Generarea previzualizării a eșuat'
    step.value = 'upload'
  } finally {
    composing.value = false
    composingPhase.value = ''
  }
}

function selectTip(index: number) {
  selectedTipIndex.value = index
  selectedAccessoryKey.value = 'none'
}

function goAccessories() {
  if (selectedTipIndex.value == null) return
  step.value = 'accessories'
}

function goPreview() {
  step.value = 'preview'
}

async function addCustomizationToCart() {
  if (!designFile.value || selectedTipIndex.value == null || !selectedAccessory.value) {
    error.value = 'Completează pașii înainte de a adăuga în coș'
    return
  }
  addingToCart.value = true
  error.value = ''
  try {
    const original = designFile.value
    // Processed (rembg) only for engraving — Design original stays the raw upload.
    const processed = processedDesignFile.value || await prepareDesignFileForUpload()
    const body = new FormData()
    body.append('design', original, original.name || 'design-original.jpg')
    if (processed && processed !== original) {
      body.append('designProcessed', processed, processed.name || 'design-processed.png')
    }
    body.append('tipIndex', String(selectedTipIndex.value))
    body.append('batteries', selectedAccessory.value.batteries)
    body.append('adaptor', selectedAccessory.value.adaptor)
    body.append('preset', designPreset.value)
    // Send already-composed preview so server skips dithering/compose again.
    const previewUrl = selectedPreview.value?.previewDataUrl
    if (previewUrl?.startsWith('data:')) {
      const res = await fetch(previewUrl)
      const blob = await res.blob()
      body.append('preview', blob, 'preview.webp')
    }

    const response = await post<{
      product: Product
      variant: ProductVariant
      customFields: CustomFieldValueInput[]
      previewUrl: string
      unitPrice: number
    }>(
      '/giftster/prepare-cart-line',
      body,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60_000 },
    )

    const data = response.data
    if (!data?.product || !data?.variant) {
      throw new Error('Nu s-a putut pregăti produsul pentru coș')
    }

    const product: Product = {
      ...data.product,
      images: [
        {
          id: 0,
          productId: data.product.id,
          url: data.previewUrl || selectedPreview.value?.previewDataUrl || '',
          alt: data.product.name,
          sortOrder: 0,
          isPrimary: true,
          is360: false,
        },
        ...(data.product.images || []).filter((img) => img.url !== data.previewUrl),
      ],
    }

    await addToCart(product, 1, data.variant, {
      customFields: data.customFields || [],
      openDrawer: true,
    })
  } catch (err: unknown) {
    const message = (err as { response?: { data?: { message?: string } }; message?: string })
      ?.response?.data?.message
      || (err as { message?: string })?.message
    error.value = message || 'Nu s-a putut adăuga în coș'
  } finally {
    addingToCart.value = false
  }
}

function resetWizard() {
  step.value = 'upload'
  previews.value = []
  selectedTipIndex.value = null
  selectedAccessoryKey.value = 'none'
  designFile.value = null
  processedDesignFile.value = null
  if (designPreviewUrl.value) URL.revokeObjectURL(designPreviewUrl.value)
  designPreviewUrl.value = null
  error.value = ''
  if (fileInput.value) fileInput.value.value = ''
}

onMounted(() => {
  void loadConfig()
})

onBeforeUnmount(() => {
  if (designPreviewUrl.value) URL.revokeObjectURL(designPreviewUrl.value)
})
</script>

<template>
  <section class="max-w-7xl mx-auto px-4 py-12 md:py-16">
    <div class="mb-8 md:mb-10 max-w-2xl">
      <p class="text-sm font-medium text-primary-600 mb-2">
        Personalizare LED
      </p>
      <h2 class="text-2xl md:text-3xl font-semibold text-highlighted tracking-tight">
        Încearcă designul tău pe lampă
      </h2>
      <p class="mt-2 text-muted text-sm md:text-base leading-relaxed">
        Încarcă o poză, alege tipul și accesoriile — apoi adaugă direct în coș comanda personalizată.
      </p>
    </div>

    <div
      v-if="loadingConfig"
      class="rounded-2xl border border-default bg-default p-10 flex items-center justify-center gap-3 text-muted"
    >
      <UIcon name="i-heroicons-arrow-path" class="w-5 h-5 animate-spin text-primary-600" />
      Se încarcă simulatorul…
    </div>

    <div
      v-else-if="error && !tips.length"
      class="rounded-2xl border border-rose-200 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-900 p-6 text-rose-800 dark:text-rose-200 text-sm"
    >
      {{ error }}
    </div>

    <div
      v-else
      class="rounded-2xl border border-default bg-default overflow-hidden shadow-sm"
    >
      <div class="px-4 pt-5 md:px-8 md:pt-6 border-b border-muted bg-elevated/40">
        <CheckoutStepper
          :steps="steps"
          :current-step="currentStep"
          aria-label="Pași personalizare LED"
        />
      </div>

      <div class="p-4 md:p-8 space-y-6">
        <p
          v-if="error"
          class="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-100 dark:border-rose-900 rounded-xl px-4 py-3"
        >
          {{ error }}
        </p>

        <!-- Step 1: Upload -->
        <div v-if="step === 'upload'" class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div class="space-y-5">
            <div>
              <h3 class="text-lg font-semibold text-highlighted">
                Încarcă designul
              </h3>
              <p class="mt-1 text-sm text-muted leading-relaxed">
                PNG, JPG, WebP sau SVG. Îl transformăm în siluetă LED albă, ca pe produsele reale.
              </p>
            </div>

            <button
              type="button"
              class="w-full rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors"
              :class="isDragging
                ? 'border-primary-500 bg-primary-50/80 dark:bg-primary-950/30'
                : designFile
                  ? 'border-primary-300 bg-primary-50/40 dark:bg-primary-950/20'
                  : 'border-muted hover:border-primary-400 hover:bg-elevated/60'"
              @click="fileInput?.click()"
              @dragenter.prevent="isDragging = true"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="onDrop"
            >
              <UIcon
                name="i-heroicons-cloud-arrow-up"
                class="w-10 h-10 mx-auto mb-3 text-primary-600"
              />
              <p class="text-sm font-medium text-highlighted">
                {{ designFile ? designFile.name : 'Trage poza aici sau apasă pentru a alege' }}
              </p>
              <p class="mt-1 text-xs text-muted">
                Recomandat: contrast bun, fundal cât mai curat · maxim 50 MB
              </p>
            </button>
            <input
              ref="fileInput"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,.png,.jpg,.jpeg,.webp,.svg"
              class="hidden"
              @change="onPickFile"
            >

            <div class="space-y-3">
              <p class="text-sm font-medium text-highlighted">Tip procesare</p>
              <div class="grid grid-cols-3 gap-2">
                <button
                  v-for="opt in presetOptions"
                  :key="opt.value"
                  type="button"
                  class="rounded-xl border px-3 py-2.5 text-left transition-colors"
                  :class="designPreset === opt.value
                    ? 'border-primary-500 bg-primary-50/70 dark:bg-primary-950/40 ring-1 ring-primary-500/20'
                    : 'border-default hover:border-primary-300'"
                  @click="designPreset = opt.value"
                >
                  <span class="block text-sm font-medium text-highlighted">{{ opt.label }}</span>
                  <span class="block text-[11px] text-muted mt-0.5 leading-snug">{{ opt.hint }}</span>
                </button>
              </div>
              <p class="text-xs text-muted">
                Poze: dither tip gravură foto. Logo: siluetă potrace. Fundalul se curăță automat la poze.
              </p>
            </div>

            <UButton
              label="Generează previzualizări"
              icon="i-heroicons-sparkles"
              size="lg"
              block
              :disabled="!designFile"
              @click="startCompose"
            />
          </div>

          <div
            class="aspect-square max-w-md mx-auto w-full rounded-2xl border border-dashed border-muted bg-elevated/50 overflow-hidden flex items-center justify-center"
          >
            <img
              v-if="designPreviewUrl"
              :src="designPreviewUrl"
              alt="Design încărcat"
              class="max-h-full max-w-full object-contain p-4"
            >
            <div v-else class="text-center text-dimmed text-sm px-6">
              <UIcon name="i-heroicons-photo" class="w-12 h-12 mx-auto mb-3 opacity-50" />
              Previzualizarea designului apare aici
            </div>
          </div>
        </div>

        <!-- Composing -->
        <div
          v-else-if="step === 'composing'"
          class="py-20 flex flex-col items-center justify-center gap-3 text-muted"
        >
          <UIcon name="i-heroicons-arrow-path" class="w-9 h-9 animate-spin text-primary-600" />
          <p class="font-semibold text-highlighted text-lg text-center px-4 max-w-md">
            {{ composingPhase || 'Pregătim designul…' }}
          </p>
          <p class="text-sm text-center max-w-sm text-dimmed">
            Poate dura câteva zeci de secunde — te rugăm să aștepți.
          </p>
        </div>

        <!-- Step 2: Tip -->
        <div v-else-if="step === 'tip'" class="space-y-5">
          <div>
            <h3 class="text-lg font-semibold text-highlighted">
              Alege tipul de produs
            </h3>
            <p class="mt-1 text-sm text-muted">
              Vezi cum arată designul pe fiecare variantă — prețul e cel din magazin.
            </p>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            <button
              v-for="preview in previews"
              :key="preview.tipIndex"
              type="button"
              class="group text-left rounded-2xl border overflow-hidden transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              :class="selectedTipIndex === preview.tipIndex
                ? 'border-primary-500 ring-2 ring-primary-500/25'
                : 'border-default hover:border-primary-300'"
              @click="selectTip(preview.tipIndex)"
            >
              <div class="aspect-square bg-elevated">
                <img
                  :src="preview.previewDataUrl"
                  :alt="preview.shortName"
                  class="h-full w-full object-cover"
                >
              </div>
              <div class="p-3">
                <p class="text-xs font-medium text-highlighted line-clamp-2 leading-snug">
                  {{ preview.shortName }}
                </p>
                <p class="text-sm text-primary-700 dark:text-primary-400 font-semibold mt-1.5">
                  {{ formatPrice(preview.basePrice) }}
                </p>
              </div>
            </button>
          </div>

          <div class="flex flex-wrap gap-3 pt-2">
            <UButton label="Înapoi" variant="outline" color="neutral" @click="resetWizard" />
            <UButton
              label="Continuă"
              icon="i-heroicons-arrow-right"
              trailing
              :disabled="selectedTipIndex == null"
              @click="goAccessories"
            />
          </div>
        </div>

        <!-- Step 3: Accessories -->
        <div v-else-if="step === 'accessories'" class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div class="space-y-5">
            <div>
              <h3 class="text-lg font-semibold text-highlighted">
                Accesorii
              </h3>
              <p class="mt-1 text-sm text-muted">
                Opțional — disponibilitatea depinde de tipul ales.
              </p>
            </div>

            <div class="flex flex-col gap-2.5">
              <button
                v-for="opt in accessoryOptions"
                :key="opt.key"
                type="button"
                class="flex items-center justify-between rounded-2xl border px-4 py-3.5 text-left transition-colors"
                :class="selectedAccessoryKey === opt.key
                  ? 'border-primary-500 bg-primary-50/70 dark:bg-primary-950/40 ring-1 ring-primary-500/20'
                  : 'border-default hover:border-primary-300'"
                @click="selectedAccessoryKey = opt.key"
              >
                <span class="text-sm font-medium text-highlighted">{{ opt.label }}</span>
                <span class="text-sm font-semibold text-toned tabular-nums">
                  {{ opt.surcharge > 0 ? `+${formatPrice(opt.surcharge)}` : 'inclus' }}
                </span>
              </button>
            </div>

            <div class="flex flex-wrap gap-3 pt-1">
              <UButton label="Înapoi" variant="outline" color="neutral" @click="step = 'tip'" />
              <UButton
                label="Vezi sumarul"
                icon="i-heroicons-arrow-right"
                trailing
                @click="goPreview"
              />
            </div>
          </div>

          <div
            v-if="selectedPreview"
            class="rounded-2xl overflow-hidden border border-default bg-elevated aspect-square"
          >
            <img
              :src="selectedPreview.previewDataUrl"
              :alt="selectedPreview.shortName"
              class="h-full w-full object-contain"
            >
          </div>
        </div>

        <!-- Step 4: Final -->
        <div v-else-if="step === 'preview'" class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div
            v-if="selectedPreview"
            class="rounded-2xl overflow-hidden border border-default bg-elevated aspect-square order-2 lg:order-1"
          >
            <img
              :src="selectedPreview.previewDataUrl"
              :alt="selectedPreview.shortName"
              class="h-full w-full object-contain"
            >
          </div>

          <div class="space-y-6 flex flex-col justify-center order-1 lg:order-2">
            <div>
              <h3 class="text-lg font-semibold text-highlighted mb-4">
                Sumar comandă personalizată
              </h3>
              <dl class="space-y-4">
                <div class="flex items-start justify-between gap-4 border-b border-muted pb-3">
                  <dt class="text-sm text-muted">Tip produs</dt>
                  <dd class="text-sm font-semibold text-highlighted text-right">
                    {{ selectedTip?.shortName }}
                  </dd>
                </div>
                <div class="flex items-start justify-between gap-4 border-b border-muted pb-3">
                  <dt class="text-sm text-muted">Accesorii</dt>
                  <dd class="text-sm font-semibold text-highlighted text-right">
                    {{ selectedAccessory?.label }}
                  </dd>
                </div>
                <div class="flex items-end justify-between gap-4 pt-1">
                  <dt class="text-sm text-muted">Total</dt>
                  <dd class="text-3xl font-bold text-primary-700 dark:text-primary-400 tabular-nums">
                    {{ formatPrice(totalPrice) }}
                  </dd>
                </div>
              </dl>
            </div>

            <div class="flex flex-col sm:flex-row flex-wrap gap-3">
              <UButton
                label="Adaugă în coș"
                icon="i-heroicons-shopping-bag"
                size="lg"
                :loading="addingToCart"
                :disabled="addingToCart"
                @click="addCustomizationToCart"
              />
              <UButton
                label="Schimbă tipul"
                variant="outline"
                color="neutral"
                :disabled="addingToCart"
                @click="step = 'tip'"
              />
              <UButton
                label="Altă poză"
                variant="ghost"
                :disabled="addingToCart"
                @click="resetWizard"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
